import React, { useCallback, useState } from 'react';

export const FileUpload = ({ onFilesUpload }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const [selectedFiles, setSelectedFiles] = useState([]);

  const viewTypes = [
    { value: 'front', label: 'Front View', icon: '' },
    { value: 'back', label: 'Back View', icon: '' },
    { value: 'left', label: 'Left Side', icon: '' },
    { value: 'right', label: 'Right Side', icon: '' },
    { value: 'closeup', label: 'Close Up', icon: '' },
    { value: 'lifestyle', label: 'Lifestyle', icon: '' },
    { value: 'other', label: 'Other', icon: '' }
  ];

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      const filesWithViews = filesArray.map((file, index) => ({
        file,
        viewType: viewTypes[Math.min(index, viewTypes.length - 1)].value, // Auto-assign views
        customLabel: ''
      }));
      setSelectedFiles(filesWithViews);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files);
      const filesWithViews = filesArray.map((file, index) => ({
        file,
        viewType: viewTypes[Math.min(index, viewTypes.length - 1)].value,
        customLabel: ''
      }));
      setSelectedFiles(filesWithViews);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleViewTypeChange = (index, newViewType) => {
    setSelectedFiles(prev => prev.map((file, i) => 
      i === index ? { ...file, viewType: newViewType } : file
    ));
  };

  const handleCustomLabelChange = (index, customLabel) => {
    setSelectedFiles(prev => prev.map((file, i) => 
      i === index ? { ...file, customLabel } : file
    ));
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
  };

  const handleUpload = () => {
    if (selectedFiles.length > 0) {
      onFilesUpload(selectedFiles);
    }
  };

  const getViewLabel = (viewType, customLabel) => {
    if (customLabel) return customLabel;
    const view = viewTypes.find(v => v.value === viewType);
    return view ? view.label : 'Unknown View';
  };

  const getViewIcon = (viewType) => {
    const view = viewTypes.find(v => v.value === viewType);
    return view ? view.icon : '📎';
  };

  // ... (keep the existing handleUrlSubmit and convertWebPToJPG functions)

const handleUrlSubmit = async (e) => {
  e.preventDefault();
  if (!imageUrl.trim()) return;

  setIsLoading(true);
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    let extension = 'jpg';
    const urlObj = new URL(imageUrl);
    const pathname = urlObj.pathname;

    const urlExtension = pathname.split('.').pop()?.toLowerCase();
    if (urlExtension && ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(urlExtension)) {
      extension = urlExtension === 'jpeg' ? 'jpg' : urlExtension;
    } else {
      const blobType = blob.type.split('/')[1];
      if (blobType && ['jpeg', 'png', 'webp', 'gif'].includes(blobType)) {
        extension = blobType === 'jpeg' ? 'jpg' : blobType;
      }
    }

    let finalExtension = extension === 'webp' ? 'jpg' : extension;
    let finalBlob = extension === 'webp' ? await convertWebPToJPG(blob) : blob;
    
    const file = new File([finalBlob], `downloaded-image.${finalExtension}`, {
      type: finalExtension === 'jpg' ? 'image/jpeg' : `image/${finalExtension}`
    });

    // ✅ FIX: send in array format with view object
    onFilesUpload([
      {
        file,
        viewType: 'front', // or auto assign
        customLabel: ''
      }
    ]);

    setImageUrl('');
  } catch (error) {
    console.error('Error downloading image:', error);
    alert('Failed to download image. Please check the URL and try again.');
  } finally {
    setIsLoading(false);
  }
};


const convertWebPToJPG = (webpBlob) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      if (ctx) ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert WebP to JPG'));
        }
      }, 'image/jpeg', 0.9);
    };

    img.onerror = () => reject(new Error('Failed to load WebP image'));
    img.src = URL.createObjectURL(webpBlob);
  });
};


  return (
    <div className="max-w-4xl mx-auto mt-16 flex justify-center items-center "> {/* Increased max-width */}
      <div className="w-full bg-[#1a0b4e] rounded-2xl p-6 shadow-2xl">
        <div className="flex border-b border-[#270b79] mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('upload')}
            className={`flex-1 py-3 font-medium text-lg transition-all duration-300 ${
              activeTab === 'upload'
                ? 'text-[#F06B32] border-b-2 border-[#F06B32]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Upload Files
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('url')}
            className={`flex-1 py-3 font-medium text-lg transition-all duration-300 ${
              activeTab === 'url'
                ? 'text-[#F06B32] border-b-2 border-[#F06B32]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Image URL
          </button>
        </div>

        {activeTab === 'upload' && (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`p-8 border-4 border-dashed rounded-2xl transition-all duration-300 ${
              isDragging 
                ? 'border-[#F06B32] bg-[#270b79] bg-opacity-20' 
                : 'border-[#270b79] hover:border-[#F06B32] hover:bg-[#270b79] hover:bg-opacity-10'
            }`}
          >
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 bg-[#270b79] rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>
              </div>
              <p className="text-lg text-white font-medium mb-2 text-center">
                Drag & drop multiple product photos (different angles)
              </p>
              <p className="text-gray-300 mb-4">or</p>
              <label htmlFor="file-upload" className="cursor-pointer bg-[#F06B32] hover:bg-[#e55a27] text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 shadow-lg mb-6">
                Browse Files
              </label>
              <input 
                id="file-upload" 
                name="file-upload" 
                type="file" 
                className="sr-only" 
                accept="image/*" 
                multiple
                onChange={handleFileChange} 
              />
              
              {/* Selected files with view selection */}
              {selectedFiles.length > 0 && (
                <div className="mt-6 w-full">
                  <p className="text-white font-medium mb-3">Assign views to your images:</p>
                  <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                    {selectedFiles.map((fileData, index) => (
                      <div key={index} className="bg-[#270b79] bg-opacity-30 rounded-xl p-4 border border-[#F06B32] border-opacity-30">
                        <div className="flex flex-col sm:flex-row gap-4 items-start">
                          {/* Image Preview */}
                          <div className="flex-shrink-0">
                            <img 
                              src={URL.createObjectURL(fileData.file)} 
                              alt={`Preview ${index + 1}`}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          </div>
                          
                          {/* View Selection */}
                          <div className="flex-1 space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                View Type:
                              </label>
                              <select
                                value={fileData.viewType}
                                onChange={(e) => handleViewTypeChange(index, e.target.value)}
                                className="w-full bg-[#0f0835] border border-[#270b79] rounded-lg text-white text-sm p-2 focus:outline-none focus:border-[#F06B32]"
                              >
                                {viewTypes.map(view => (
                                  <option key={view.value} value={view.value}>
                                    {view.icon} {view.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Custom Label */}
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                Custom Label (optional):
                              </label>
                              <input
                                type="text"
                                value={fileData.customLabel}
                                onChange={(e) => handleCustomLabelChange(index, e.target.value)}
                                placeholder="e.g., Front with model, Back detail, etc."
                                className="w-full bg-[#0f0835] border border-[#270b79] rounded-lg text-white text-sm p-2 focus:outline-none focus:border-[#F06B32]"
                              />
                            </div>
                            
                            {/* File Info and Remove */}
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-400 truncate max-w-[200px]">
                                {fileData.file.name}
                              </span>
                              <button
                                onClick={() => removeFile(index)}
                                className="text-red-400 hover:text-red-300 text-sm font-medium"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Upload Button */}
                  <div className="mt-6 flex justify-center">
                    <button
                      onClick={handleUpload}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 shadow-lg"
                    >
                      ✅ Upload {selectedFiles.length} Image{selectedFiles.length !== 1 ? 's' : ''}
                    </button>
                  </div>
                </div>
              )}
              
              <p className="text-gray-400 text-sm mt-4 text-center">
                Upload multiple angles: Front, Back, Side views, etc. Supports: JPG, PNG, WebP
              </p>
            </div>
          </div>
        )}

        {/* Keep the URL tab content the same */}
        {activeTab === 'url' && (
                    <div className="p-8 border-4 border-dashed border-[#270b79] rounded-2xl">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 bg-[#270b79] rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
              </div>
              <p className="text-lg text-white font-medium mb-4 text-center">
                Paste your image URL
              </p>
              <form onSubmit={handleUrlSubmit} className="w-full max-w-md">
                <div className="flex flex-col space-y-4">
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-3 bg-[#0f0835] border border-[#270b79] rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-[#F06B32] focus:ring-2 focus:ring-[#F06B32] focus:ring-opacity-50 transition-all duration-300"
                    required
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !imageUrl.trim()}
                    className="w-full bg-[#F06B32] hover:bg-[#e55a27] disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 shadow-lg"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Downloading & Converting...
                      </div>
                    ) : (
                      'Download & Convert Image'
                    )}
                  </button>
                </div>
              </form>
              <p className="text-gray-400 text-sm mt-4 text-center">
                Supports URLs from any source. WebP images will be automatically converted to JPG.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};